import { buyerFetch } from "@/lib/buyerClient";
import { type ReservationStatus } from "@/features/storefront";

/**
 * TNK-167 phase C — typed wrappers around the /api/buyer/* surface.
 *
 * Pattern mirrors features/storefront.ts: thin async functions that throw
 * on non-2xx with the server's error string when present, so callers can
 * surface the message in a banner without re-parsing the response.
 */

export interface BuyerProfile {
  id: string;
  email: string;
  name: string | null;
}

export interface BuyerReservationSummary {
  id: string;
  statusToken: string;
  status: string;
  requestedAt: string;
  expiresAt: string;
  resolvedAt: string | null;
  pickupPreference: "pickup" | "ship" | "either";
  itemCount: number;
  currentOffer: {
    id: string;
    total: number;
    status: string;
    expiresAt: string;
  } | null;
}

/** Calls `/api/buyer/me`. Never throws — returns null on unauth or
 *  network blip. The server always returns 200 here. */
export async function fetchBuyerMe(): Promise<BuyerProfile | null> {
  try {
    const res = await buyerFetch("/api/buyer/me");
    if (!res.ok) return null;
    const body = (await res.json()) as { buyer: BuyerProfile | null };
    return body.buyer;
  } catch {
    return null;
  }
}

export async function startBuyerSignin(email: string): Promise<void> {
  const res = await buyerFetch("/api/buyer/auth/start", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(
      body.error ?? `Could not send sign-in link (${res.status}).`,
    );
  }
}

/** Guest cart carried through the first magic link (TNK-202). */
export interface ReserveHandoff {
  cardIds: string[];
  /** TNK-212 phase 2b — locked-bundle product ids reserved alongside cards. */
  productIds?: string[];
  buyerName?: string;
  buyerNote?: string;
  pickupPreference?: string;
  shippingAddress?: Record<string, string>;
}

/** Start a guest reserve: send the first magic link carrying the cart so the
 *  reservation is created on verify (TNK-202). Same endpoint as plain sign-in,
 *  with a `reserve` payload. */
export async function startBuyerReserve(
  email: string,
  reserve: ReserveHandoff,
): Promise<void> {
  const res = await buyerFetch("/api/buyer/auth/start", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ email, reserve }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(
      body.error ?? `Could not send sign-in link (${res.status}).`,
    );
  }
}

export async function completeBuyerSignin(token: string): Promise<{
  buyer: BuyerProfile;
  reservePayload: ReserveHandoff | null;
}> {
  const res = await buyerFetch("/api/buyer/auth/complete", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token }),
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `Could not sign in (${res.status}).`);
  }
  const data = (await res.json()) as {
    buyer: BuyerProfile;
    reservePayload?: ReserveHandoff | null;
  };
  return { buyer: data.buyer, reservePayload: data.reservePayload ?? null };
}

export async function buyerSignOut(): Promise<void> {
  await buyerFetch("/api/buyer/logout", { method: "POST" });
}

export async function fetchBuyerReservations(): Promise<
  BuyerReservationSummary[]
> {
  const res = await buyerFetch("/api/buyer/reservations");
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(
      body.error ?? `Could not load reservations (${res.status}).`,
    );
  }
  const data = (await res.json()) as {
    reservations: BuyerReservationSummary[];
  };
  return data.reservations;
}

/** Tagged result for the account-scoped detail fetch — lets the page show the
 *  right message for each gated response instead of a generic error. */
export type BuyerReservationResult =
  | { ok: true; reservation: ReservationStatus & { statusToken: string } }
  | { ok: false; reason: "notfound" | "auth" | "forbidden" | "error" };

/** Account-scoped reservation detail by id. Maps the server's gated responses:
 *  404 → notfound, 401 → auth (not signed in), 403 → forbidden (belongs to a
 *  different buyer). Same payload as the public token page plus `statusToken`,
 *  which the detail view uses to accept/decline offers. */
export async function fetchBuyerReservation(
  id: string,
): Promise<BuyerReservationResult> {
  try {
    const res = await buyerFetch(
      `/api/buyer/reservations/${encodeURIComponent(id)}`,
    );
    if (res.ok) {
      const reservation = (await res.json()) as ReservationStatus & {
        statusToken: string;
      };
      return { ok: true, reservation };
    }
    if (res.status === 404) return { ok: false, reason: "notfound" };
    if (res.status === 401) return { ok: false, reason: "auth" };
    if (res.status === 403) return { ok: false, reason: "forbidden" };
    return { ok: false, reason: "error" };
  } catch {
    return { ok: false, reason: "error" };
  }
}
